import { cn } from "@scribenote/brian-design-system";

import type { CaptureStatus } from "./captureNodeStatus";

/**
 * Small per-room capture indicator for the clinic board (WEB-3252): a status-ink
 * dot conveying whether the room's Mike/Cameron node is recording, online but
 * idle, offline, or of unknown state because Zuul's link is down. `none` (no
 * node) renders nothing - most clinics won't have a node in every room, and a
 * dot for "no capture here" would be noise.
 *
 * A bare dot, deliberately: this is a device indicator, not a status word, the
 * established exception to the wash rule. The word lives in the title and
 * aria-label rather than under a highlighter.
 */
// ink-working = capture healthy; the bds-pulse blink marks a node actively
// recording right now. ink-flag = unknown (link down), faint = offline.
const PRESENTATION: Record<
  Exclude<CaptureStatus, "none">,
  { dot: string; label: string; pulse: boolean }
> = {
  recording: { dot: "bg-ink-working", label: "Recording", pulse: true },
  online: {
    dot: "bg-ink-working",
    label: "Capture online (idle)",
    pulse: false,
  },
  offline: { dot: "bg-faint", label: "Capture offline", pulse: false },
  stale: {
    dot: "bg-ink-flag",
    label: "Capture status unknown - link down",
    pulse: false,
  },
};

export const CaptureStatusDot = ({
  status,
  roomId,
}: {
  status: CaptureStatus;
  roomId: string;
}) => {
  if (status === "none") return null;
  const { dot, label, pulse } = PRESENTATION[status];
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      data-testid={`capture-status-${roomId}`}
      data-status={status}
      className="relative flex h-2.5 w-2.5 shrink-0 items-center justify-center"
    >
      <span
        aria-hidden="true"
        className={cn(
          "relative inline-flex h-2 w-2 rounded-full",
          dot,
          pulse &&
            "[animation:bds-pulse_1.6s_ease-in-out_infinite] motion-reduce:animate-none",
        )}
      />
    </span>
  );
};
